import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { PurchaseOrder } from '../../../src/services/procurement.service';

interface PurchaseOrderApprovalModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  order: PurchaseOrder | null;
  approvalId: string | null;
  onApprove: (approvalId: string, comments: string) => Promise<void>;
  onReject: (approvalId: string, rejectionReason: string, comments: string) => Promise<void>;
}

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

export function PurchaseOrderApprovalModal({
  open,
  onOpenChange,
  order,
  approvalId,
  onApprove,
  onReject
}: PurchaseOrderApprovalModalProps) {
  const [comments, setComments] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [action, setAction] = useState<'approve' | 'reject' | null>(null);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!open) {
      setComments('');
      setRejectionReason('');
      setAction(null);
      setIsSubmitting(false);
    }
  }, [open]);

  const handleApprove = async () => {
    if (!approvalId) return;

    setAction('approve');
    setIsSubmitting(true);
    try {
      await onApprove(approvalId, comments);
      onOpenChange(false);
    } catch (error) {
      console.error('Approval error:', error);
    } finally {
      setIsSubmitting(false);
      setAction(null);
    }
  };

  const handleReject = async () => {
    if (!approvalId || !rejectionReason.trim()) return;

    setAction('reject');
    setIsSubmitting(true);
    try {
      await onReject(approvalId, rejectionReason.trim(), comments);
      onOpenChange(false);
    } catch (error) {
      console.error('Rejection error:', error);
    } finally {
      setIsSubmitting(false);
      setAction(null);
    }
  };

  if (!order) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-yellow-600" />
            Purchase Order Approval
          </DialogTitle>
          <DialogDescription>
            Review the purchase order details and provide your decision
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* PO Summary */}
          <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
            <div>
              <p className="text-sm text-muted-foreground">PO Number</p>
              <p className="font-semibold">{order.po_number}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Status</p>
              <Badge className="bg-yellow-100 text-yellow-800">
                Pending Approval
              </Badge>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Vendor</p>
              <p className="font-semibold">{order.vendor_name}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Amount</p>
              <p className="font-semibold text-lg">{formatCurrency(Number(order.total_amount))}</p>
            </div>
            <div className="col-span-2">
              <p className="text-sm text-muted-foreground">Description</p>
              <p className="text-sm">{order.description || 'No description provided'}</p>
            </div>
            {order.items && order.items.length > 0 && (
              <div className="col-span-2">
                <p className="text-sm text-muted-foreground mb-2">Line Items</p>
                <div className="text-sm space-y-1">
                  {order.items.slice(0, 3).map((item, idx) => (
                    <div key={idx} className="flex justify-between">
                      <span>{item.description || item.item_name}</span>
                      <span className="font-medium">
                        {item.quantity_ordered} × {formatCurrency(item.unit_price)}
                      </span>
                    </div>
                  ))}
                  {order.items.length > 3 && (
                    <p className="text-muted-foreground italic">
                      +{order.items.length - 3} more items
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Comments (optional for approval, required context for reject) */}
          <div className="space-y-2">
            <Label htmlFor="comments">
              Comments {action === 'reject' ? '(Optional)' : '(Optional)'}
            </Label>
            <Textarea
              id="comments"
              placeholder="Add any notes or comments about this approval decision..."
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              rows={3}
              disabled={isSubmitting}
            />
          </div>

          {/* Rejection Reason (only shown when rejecting) */}
          {action === 'reject' && (
            <div className="space-y-2">
              <Label htmlFor="rejectionReason" className="text-red-600">
                Rejection Reason <span className="text-red-600">*</span>
              </Label>
              <Textarea
                id="rejectionReason"
                placeholder="Please provide a clear reason for rejecting this purchase order..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={3}
                disabled={isSubmitting}
                className="border-red-200 focus:border-red-400"
              />
              {!rejectionReason.trim() && (
                <p className="text-sm text-red-600">Rejection reason is required</p>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>

          {action !== 'reject' && (
            <>
              <Button
                variant="destructive"
                onClick={() => setAction('reject')}
                disabled={isSubmitting}
              >
                <XCircle className="h-4 w-4 mr-2" />
                Reject
              </Button>
              <Button
                variant="default"
                onClick={handleApprove}
                disabled={isSubmitting}
                className="bg-green-600 hover:bg-green-700"
              >
                {isSubmitting && action === 'approve' ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Approving...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Approve
                  </>
                )}
              </Button>
            </>
          )}

          {action === 'reject' && (
            <>
              <Button
                variant="outline"
                onClick={() => setAction(null)}
                disabled={isSubmitting}
              >
                Back
              </Button>
              <Button
                variant="destructive"
                onClick={handleReject}
                disabled={isSubmitting || !rejectionReason.trim()}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Rejecting...
                  </>
                ) : (
                  <>
                    <XCircle className="h-4 w-4 mr-2" />
                    Confirm Rejection
                  </>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
